import { Link, useNavigate } from "react-router-dom";
import { Breadcrumb, Typography } from "antd";

const { Title } = Typography;

const GOLD = "#C5A028";

// Shared layout for FAQ / Returns / Privacy. Matches the site-theme banner
// pattern used by About / Contact / Craftsmanship: light grey breadcrumb
// bar, white-on-desktop / black-on-mobile (≤991px) header section, gold
// eyebrow, uppercase 32px title, gradient gold rule. Children render
// inside a typography-styled "prose" container so admin-typed HTML
// (h1-h6, p, ul, ol, a, strong, em) all read well.
const styles = `
  .pl-page { background: #fff; color: #1a1a1a; padding: 0 0 90px; font-family: "Poppins", sans-serif; }

  .pl-bcBar { background: #f7f7f7; border-bottom: 1px solid #eee; padding: 16px 0; text-align: center; }
  .pl-bcInner { max-width: 1400px; margin: 0 auto; padding: 0 40px; }
  @media (max-width: 768px) { .pl-bcInner { padding: 0 16px; } }
  .pl-bcInner .ant-breadcrumb { font-size: 12px; font-weight: 500; letter-spacing: 0.3px; }

  .pl-header { text-align: center; padding: 50px 20px 40px; }
  @media (max-width: 991px) { .pl-header { background: #111; padding: 36px 20px 32px; } }
  .pl-eyebrow { display: block; font-size: 11px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: ${GOLD}; margin-bottom: 12px; }
  .pl-title { font-size: 32px !important; font-weight: 700 !important; letter-spacing: 3px; text-transform: uppercase; color: #111 !important; margin: 0 0 18px !important; border: none !important; }
  @media (max-width: 991px) { .pl-title { color: #fff !important; } }
  @media (max-width: 425px) { .pl-title { font-size: 24px !important; letter-spacing: 2px; } }
  .pl-rule { width: 50px; height: 2px; background: linear-gradient(90deg, transparent, ${GOLD}, #D4AF37, ${GOLD}, transparent); margin: 0 auto; }
  @media (max-width: 991px) { .pl-rule { display: none; } }

  .pl-intro { max-width: 760px; margin: 22px auto 0; font-size: 15px; line-height: 1.7; color: #666; padding: 0 20px; font-weight: 300; }
  @media (max-width: 991px) { .pl-intro { color: #ccc; } }

  /* Body container — generous reading width, restrained typography. */
  .pl-body { max-width: 820px; margin: 60px auto 0; padding: 0 24px; }
  @media (max-width: 991px) { .pl-body { margin-top: 40px; padding: 0 18px; } }

  /* Prose styles applied to admin-typed HTML. */
  .pl-prose, .pl-prose p, .pl-prose li, .pl-prose td { font-size: 15px; line-height: 1.85; color: #444; font-weight: 300; }
  .pl-prose h1, .pl-prose h2, .pl-prose h3, .pl-prose h4 { color: #111; font-weight: 700; letter-spacing: 1px; }
  .pl-prose h1 { font-size: 24px; margin: 48px 0 16px; padding-top: 24px; border-top: 1px solid #eee; }
  .pl-prose h1:first-child { padding-top: 0; border-top: none; }
  .pl-prose h2 { font-size: 19px; text-transform: uppercase; letter-spacing: 1.5px; margin: 40px 0 14px; padding-top: 20px; border-top: 1px solid #f0f0f0; }
  .pl-prose h2:first-child { padding-top: 0; border-top: none; }
  .pl-prose h3 { font-size: 16px; margin: 30px 0 10px; color: ${GOLD}; letter-spacing: 1px; }
  .pl-prose h4 { font-size: 14px; margin: 24px 0 8px; }
  .pl-prose p { margin: 0 0 18px; }
  .pl-prose ul, .pl-prose ol { margin: 0 0 18px; padding-left: 22px; }
  .pl-prose li { margin: 6px 0; }
  .pl-prose ul li::marker { color: ${GOLD}; }
  .pl-prose strong, .pl-prose b { color: #111; font-weight: 600; }
  .pl-prose a { color: ${GOLD}; text-decoration: underline; text-underline-offset: 3px; }
  .pl-prose a:hover { color: #b58d1f; }
  .pl-prose hr { border: none; height: 1px; background: #eee; margin: 32px 0; }
  .pl-prose blockquote { margin: 22px 0; padding: 14px 22px; border-left: 3px solid ${GOLD}; background: #fafaf6; font-style: italic; color: #555; }
  .pl-prose table { width: 100%; border-collapse: collapse; margin: 22px 0; }
  .pl-prose th, .pl-prose td { padding: 10px 14px; border-bottom: 1px solid #eee; text-align: left; }
  .pl-prose th { color: #111; font-weight: 600; background: #fafafa; }

  /* Empty-state when content hasn't been set in admin */
  .pl-empty { max-width: 760px; margin: 60px auto 0; padding: 60px 24px; text-align: center; color: #999; font-size: 13px; line-height: 1.7; background: #fafaf6; border: 1px solid #eee; }

  /* Bottom contact card */
  .pl-cta { max-width: 820px; margin: 60px auto 0; padding: 36px 28px; text-align: center; background: #fafaf6; border: 1px solid #eee; }
  .pl-cta h3 { font-size: 16px; letter-spacing: 1.5px; text-transform: uppercase; font-weight: 700; color: #111; margin: 0 0 8px; }
  .pl-cta p { font-size: 13px; color: #666; margin: 0 0 20px; }
  .pl-cta-btn { display: inline-block; padding: 12px 28px; background: #111; color: #fff; font-size: 11px; letter-spacing: 2.5px; font-weight: 600; text-transform: uppercase; cursor: pointer; border: none; font-family: inherit; transition: background 0.25s ease; text-decoration: none; }
  .pl-cta-btn:hover { background: ${GOLD}; }
`;

export default function PolicyLayout({
  breadcrumb,            // string — last breadcrumb segment
  eyebrow,               // small uppercase eyebrow above the title
  title,                 // page title
  intro,                 // optional 1-line lead under the title
  htmlBlocks = [],       // array of HTML strings to render in order
  fallback,              // shown if every htmlBlocks entry is empty
  showContactCta = true, // show "questions?" card at the bottom
  contactCtaText = "Have questions? Our team is happy to help.",
}) {
  const navigate = useNavigate();
  const hasContent = htmlBlocks.some((b) => b && b.trim());

  return (
    <div className="pl-page">
      <style>{styles}</style>

      <div className="pl-bcBar">
        <div className="pl-bcInner">
          <Breadcrumb
            items={[
              { title: <Link to="/" style={{ color: "#666", textDecoration: "none" }}>Home</Link> },
              { title: breadcrumb },
            ]}
          />
        </div>
      </div>

      <div className="pl-header">
        {eyebrow && <span className="pl-eyebrow">{eyebrow}</span>}
        <Title level={2} className="pl-title">{title}</Title>
        <div className="pl-rule" />
        {intro && <p className="pl-intro">{intro}</p>}
      </div>

      {hasContent ? (
        <div className="pl-body">
          <div className="pl-prose">
            {htmlBlocks.filter((b) => b && b.trim()).map((html, i) => (
              <div key={i} dangerouslySetInnerHTML={{ __html: html }} />
            ))}
          </div>
        </div>
      ) : (
        <div className="pl-empty">{fallback || "Content for this page hasn't been added yet."}</div>
      )}

      {showContactCta && (
        <div className="pl-cta">
          <h3>Need a hand?</h3>
          <p>{contactCtaText}</p>
          <button className="pl-cta-btn" onClick={() => navigate("/contact")}>Contact Us</button>
        </div>
      )}
    </div>
  );
}
